/// <reference types="@types/googlemaps" />
import { Component, OnInit, ViewChild, OnDestroy, ElementRef, NgZone, WritableSignal, signal } from '@angular/core'
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser'
import { OrderService } from 'src/app/services/order.service'
import { FormBuilder, FormControl, FormGroup, FormGroupDirective, ReactiveFormsModule, Validators } from '@angular/forms'
import { Subscription, merge } from 'rxjs'
import { environment } from 'src/environments/environment'
import { TagService } from 'src/app/services/tag.service'
import { ShippingService } from 'src/app/services/shipping.service'
import { Router, RouterModule } from '@angular/router'
import { ShippingOptionsComponent } from '../../../components/shipping.options/shipping.options.component'
import { GoogleAddressComponent } from 'src/app/components/google.address/google.address.component'
import { Address, ShippingForm, ShippingOption } from 'src/app/types/shipping'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { MatButtonModule } from '@angular/material/button'
import { TravelerForm, TravelerProductForm } from 'src/app/types/traveler'
import { MatFormField } from '@angular/material/form-field'
import { MatInputModule } from '@angular/material/input'
import { MatCheckboxModule } from '@angular/material/checkbox'
import { MilitaryAddressComponent } from 'src/app/components/military.address/military.address.component'
import * as _ from 'lodash'
import { CommonService } from 'src/app/services/common.service'
import { MatSelectModule } from '@angular/material/select'
import { Location } from 'src/app/types/products'
import { LocationSelectComponent } from 'src/app/components/location.select/location.select.component'
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete'
import { CountryCode, getCountryCallingCode } from 'libphonenumber-js'
import * as libphonenumber from 'google-libphonenumber'
import { NgxMaskDirective, NgxMaskPipe, provideNgxMask } from 'ngx-mask'

@Component({
    selector: 'app-shipping',
    templateUrl: './shipping.component.html',
    styleUrls: ['./shipping.component.scss'],
    imports: [
        GoogleAddressComponent,
        MatButtonModule,
        MatProgressSpinnerModule,
        ReactiveFormsModule,
        RouterModule,
        ShippingOptionsComponent,
        MatFormField,
        MatInputModule,
        MatCheckboxModule,
        MilitaryAddressComponent,
        MatSelectModule,
        LocationSelectComponent,
        NgxMaskDirective,
        NgxMaskPipe
    ],
    providers: [provideNgxMask()]
})

export class ShippingComponent implements OnInit, OnDestroy {
	@ViewChild('shippingForm') shippingForm: FormGroupDirective
	@ViewChild('shippingAddressInput') shippingAddressInput: ElementRef

	protected readonly isIdpEnv: boolean = environment.source.domain === 'idp'
	protected readonly fedex_logo: boolean = environment.fedex_logo
	protected readonly domain: string = environment.source.domain
	private shipping_required: WritableSignal<boolean>
	public mapURL: SafeResourceUrl
	public shipping: FormGroup<ShippingForm>
	public shipping_type: WritableSignal<string> = signal('basic')
	public show_shipping: WritableSignal<boolean> = signal(false)
	public ready_for_dynamic: WritableSignal<boolean> = signal(false)
	public inbound_dynamic_options: WritableSignal<ShippingOption[]> = signal(null)
	public outbound_dynamic_options: WritableSignal<ShippingOption[]> = signal(null)
	public loading_dynamic_options: WritableSignal<boolean> = signal(true)
	public show_inbound: WritableSignal<boolean> = signal(false)
	public submitted: boolean = false
	public subscription: Subscription
	public shipping_address
	public shipping_options: ShippingOption[] = this.shippingService.shipping_options
	public recalledAddress: string
	public product: FormGroup<TravelerProductForm>
	public idp: boolean = false
	public idp_zip: FormControl<string>
	public idp_changes_subscription: Subscription
	public showShippingForm: boolean = false
	public states: Location[] = []
	public countryCode: WritableSignal<string> = signal(null)
	public countryPattern: WritableSignal<string> = signal(null)

	constructor(
		private sanitizer: DomSanitizer,
		private orderService: OrderService,
		private shippingService: ShippingService,
		private tagService: TagService,
		private router: Router,
		private ngZone: NgZone,
		private fb: FormBuilder,
		private commonService: CommonService
	) {
		this.product = this.orderService.orderForm.controls.travelers.controls[0].controls.products.controls[0]
		this.shipping_options = this.shippingService.shipping_options.filter(item => item.list)
		this.shipping = this.shippingService.shippingForm
		this.showShippingForm = this.isIdpEnv && this.shipping.getRawValue().address?.address_1 ? true : false
		this.shipping_required = this.shippingService.shipping_required
		if (this.shipping.value.place_id) {
			const url = "https://www.google.com/maps/embed/v1/place?key=AIzaSyDrBuO_eyjOF8o1vZ2bdbOsc3xW6_ALheo&q=place_id:" + this.shipping.value.place_id
			this.mapURL = this.sanitizer.bypassSecurityTrustResourceUrl(url)
		}

		if (this.orderService.user) {
			let filtered = _.filter(this.orderService.user.addresses, ['type', this.shipping_required() ? 'shipping' : 'billing'])

			if (filtered.length > 0 && filtered[0].address_1) {
				this.shipping_address = filtered[0]
			}
		}
	}

	ngOnInit() {
		this.checkShipping()
		this.initIdpFunctions()
		this.tagService.utagPageView('travel:expedited travel:shipping')
		this.listenToSubmit()
	}

	ngOnDestroy() {
		if (this.subscription !== undefined) {
			this.subscription.unsubscribe()
		}

		if (this.idp_changes_subscription) {
			this.idp_changes_subscription.unsubscribe()
		}
	}

	private initIdpFunctions() {
		if (this.isIdpEnv) {
			this.setStaticValidators()
			this.loadStates()
		}
	}

	public onLocationsLoaded() {
		this.setCountryValidators(this.shipping.controls.address.controls.country.value)
	}

	private listenToSubmit() {
		this.subscription = this.orderService.submittedSubject
			.subscribe((status: boolean) => {
				this.submitted = true
				this.shippingForm.onSubmit(undefined)
				this.orderService.setFormValidity(this.shippingForm.form.valid 
					|| (this.shipping_address && this.shippingForm.form.controls.speed.valid && this.shippingForm.form.controls.inbound_speed.valid))

				if (!this.shippingForm.form.valid) {
					this.orderService.scrollToFirstError()
				}
			})
	}

	public listenToIdpChanges() {
		if (this.idp) {
			this.getDynamicShipping(true)
		}
	}

	private checkShipping() {
		let travelers = this.orderService.orderForm.getRawValue().travelers

		if (travelers.length === 1 && travelers[0].products.length === 1 
			&& ['ca_passport', 'idp', 'us_passport'].includes(travelers[0].products[0].type)) {
			const product_type = travelers[0].products[0].type

			if (product_type === 'ca_passport' && travelers[0].products[0].processing_type === 'regular') {
				// If there is only one product, it is Canadian passport, and customer is not in a rush we can offer shipping
				this.shipping_type.set('dynamic')
				this.shipping_required.set(true)
				this.show_shipping.set(true)
				this.shipping.controls.speed.setValidators([])
				this.shipping.controls.inbound_speed.setValidators([Validators.required])
	
				if (this.shipping.getRawValue().address?.address_1) {
					this.getDynamicShipping()
				}
			} else if (product_type === 'idp') {
				this.shipping_required.set(true)
				this.shipping_type.set('dynamic')
				this.show_shipping.set(true)
				this.idp = true

				const idp_traveler: FormGroup<TravelerForm> = this.orderService.orderForm.controls.travelers.controls[0]
				// IDP doesn't required inbound shipping
				this.shipping.controls.inbound_speed.setValidators([])
				const cart = localStorage.getItem('cart')
				const militaryValue = (cart && JSON.parse(cart).shipping?.is_military) || false
				this.shipping.addControl('is_military', this.fb.control(militaryValue))
				this.idp_zip = idp_traveler.controls.info.controls.dl_zip_code
				this.idp_zip.addValidators(Validators.required)
				this.idp_zip.updateValueAndValidity()

				this.getDynamicShipping()
			} else if (product_type === 'us_passport') {
				this.shipping_type.set('dynamic')
				this.shipping_required.set(true)
				this.show_shipping.set(true)
				this.shipping.controls.inbound_speed.setValidators([Validators.required])
				this.shipping.controls.speed.setValidators([Validators.required])
				this.getDynamicShipping()
			}
		} else {
			this.shipping_required.set(travelers.some((traveler) => this.shippingService.requireShippingAddress(traveler)))

			if (this.shipping_required()) {
				this.showShipping()
			} else {
				this.shipping.controls.inbound_speed.reset()
				this.shipping.controls.speed.reset()
			}
		}

		if (!this.shipping_required()) {
			this.router.navigate(['/step-4'])
		} else {
			if (this.shipping_address && this.shipping_address.address_1) {
				this.initMap()
			}
		}
	}

	public initMap() {
		this.recalledAddress = `${this.shipping_address.address_1}, ${this.shipping_address.city}, ${this.shipping_address.state} ${this.shipping_address.postal_code}, ${this.shipping_address.country}`
		const geocoder: google.maps.Geocoder = new google.maps.Geocoder() 
		 
		geocoder.geocode({ address: this.recalledAddress }, (results) => {
			this.ngZone.run(() => {
				if (results[0]) {
					this.saveAddress(results[0])
				}
			})
		})
	}

	public saveAddress(place: google.maps.GeocoderResult) {
		this.shipping.controls.place_id.patchValue(place.place_id)
		const url = "https://www.google.com/maps/embed/v1/place?key=AIzaSyDrBuO_eyjOF8o1vZ2bdbOsc3xW6_ALheo&q=place_id:" + place.place_id
		this.mapURL = this.sanitizer.bypassSecurityTrustResourceUrl(url)
		let address: any = this.shippingService.getPlaceFromGoogleObject(place)

		if (this.shipping_address && this.shipping_address.address_2) {
			address.address_2 = this.shipping_address.address_2
		}

		this.shipping.controls.address_line.patchValue(place.formatted_address, { emitEvent: false })
		this.shipping.controls.address.patchValue(address)
		this.getDynamicShipping()
	}

	public onBlur() {
		if (!this.shippingForm.value.address.address_1) {
			this.shippingForm.control.controls.address_line.setErrors({'selected': true})
		}
	}

	private showShipping() {
		const travelers = this.orderService.orderForm.getRawValue().travelers
		const show_shipping = !travelers.some((traveler) => {
			return this.shippingService.checkShipping(traveler)
		})

		this.show_shipping.set(show_shipping)

		if (show_shipping) {
			const show_inbound = this.shippingService.needsInboundShipping(travelers)
			this.show_inbound.set(show_inbound)

			if (show_inbound) {
				this.shipping.controls.inbound_speed.setValidators([Validators.required])
			}
		}
	}

	public setMapURL(event: {url: string}) {
		this.mapURL = this.sanitizer.bypassSecurityTrustResourceUrl(event.url)
	}

	public addressUpdated() {
		if (this.shipping_type() === 'dynamic') {
			if (this.isIdpEnv) {
				this.showShippingForm = true
				this.shipping.markAllAsTouched()
			}
			if (this.idp && !this.idp_zip.valid) return

			this.getDynamicShipping()
		}
	}

	public getDynamicShipping(resetValues: boolean = false) {
		const address_controls = this.shipping.controls.address.controls
		if (this.idp && 
			!(this.idp_zip.valid && 
				address_controls.address_1.valid &&
				address_controls.state.valid &&
				address_controls.country.valid &&
				address_controls.postal_code.valid &&
				address_controls.city.valid)) {
			return 
		}

		this.loading_dynamic_options.set(true)
		this.ready_for_dynamic.set(true)
				
		if (this.shipping_type() === 'dynamic') {
			if (this.idp) {
				this.shipping.controls.is_military.disable()

				if (resetValues && this.shipping.controls.speed_price) {
					this.shipping.controls.speed.reset()
					this.shipping.controls.speed_price.reset()
				}

				this.shippingService.getAAALocation(this.idp_zip.value)
					.subscribe({
						next: (response) => {
							if (response.address) {
								const to_address = this.shipping.getRawValue().address
								let couriers

								if (to_address.country === 'US') {
									couriers = {
										"fedex": ["fedex_2_day", "standard_overnight"],
										"usps": ["usps_priority_3_days", "usps_priority_overnight"]
									}
								} else {
									couriers = {
										"fedex": ["fedex_international_priority", "international_economy"],
										"usps": ["int_usps_priority", "int_usps_express"]
									}
								}

								const product_uuid = this.orderService.orderForm.controls.travelers.controls[0].controls.products.controls[0].controls.product_uuid.value

								this.shippingService.getDynamicShippingPrices(product_uuid, response.address, to_address, couriers, 'dynamic')
									.subscribe({
										next: (response) => {
											this.shipping.controls.is_military.enable()
											this.outbound_dynamic_options.set(response.data)
											this.loading_dynamic_options.set(false)
										},
										error: () => {
											//Handle another error
										}
									})
							}
						},
						error: () => {
							//Handle location error
						}
					})
			} else {
				const product = this.product.getRawValue()
				if (product.type === 'us_passport') {
					const product_uuid = this.product.getRawValue().product_uuid
					const customer_address = this.shipping.getRawValue().address
					const courier_address = {
						address_1: '919 18th Street NW',
						address_2: 'suite 230',
						city: 'Washington',
						state: 'DC',
						postal_code: '20006',
						country: 'US'
					}
					const couriers = {
						"fedex": ["fedex_international_priority", "international_economy"]
					}

					this.shippingService.getDynamicShippingPrices(product_uuid, courier_address, customer_address, couriers, 'dynamic')
						.subscribe({
							next: (response) => {
								this.outbound_dynamic_options.set(response.data)
								this.loading_dynamic_options.set(false)
							},
							error: () => {
								//Handle another error
							}
						})

					this.shippingService.getDynamicShippingPrices(product_uuid, customer_address, courier_address, couriers, 'dynamic')
					.subscribe({
						next: (response) => {
							this.inbound_dynamic_options.set(response.data)
							this.loading_dynamic_options.set(false)
						},
						error: () => {
							//Handle another error
						}
					})
				} else if (product.type === 'ca_passport') {
					this.shippingService.getDynamicShippingOptions(product.product_uuid, this.shipping.getRawValue().address)
						.subscribe({
							next: response => {
								this.inbound_dynamic_options.set(response.data)
								this.loading_dynamic_options.set(false)
							}
						})
				}
			}
		}
	}

	// While submitting in-person the Shipping form is considered valid no matter its values.
	// If the address was entered, then it is kept. Shipping options are not.
	public onSubmitInPerson(): void {
		const travelers = this.orderService.orderForm.getRawValue().travelers
		const product_type = travelers[0].products[0].type

		if (product_type === 'ca_passport' && travelers[0].products[0].processing_type === 'regular') {
			this.shipping.controls.inbound_speed.clearValidators()
			this.shipping.controls.inbound_speed.updateValueAndValidity()
		}
		this.shippingService.clearShippingOtions()
		this.orderService.setFormValidity(true)
	}

	public handleMilitaryAddress(militaryAddress: Address): void {
		this.shipping.controls.address.patchValue(militaryAddress)
		this.listenToIdpChanges()
	}

	public onMilitaryChckbxChange({checked}): void {
		this.outbound_dynamic_options.set(null)
		this.shipping.reset({is_military: checked})
		this.showShippingForm = false
		this.resetMap()

		if (checked) { 
			this.shippingService.addMilitaryAddressControl()
		} else {
			this.shipping.removeControl('military_address')
			this.shipping.controls.address_line.setValidators([Validators.required])
			this.shipping.controls.address_line.updateValueAndValidity()
		}
	}

	private loadStates() {
		this.commonService.getStates().subscribe({
			next: (data) => {
				this.states = data;
			}
		});
	}

	private setCountryValidators(selectedCountry: string) {
		const addressCtrl = this.shipping.controls.address
		const postalCodeValidators = selectedCountry === 'US' ? [Validators.required, Validators.pattern('^\\d{5}$')] : [Validators.required]
		addressCtrl.controls.postal_code.setValidators(postalCodeValidators)
		addressCtrl.controls.postal_code.updateValueAndValidity()

		if (selectedCountry !== 'US') {
			const callingCode = getCountryCallingCode(selectedCountry as CountryCode)
			this.countryCode.set(`+${callingCode}`)

			const phoneUtil: any = libphonenumber.PhoneNumberUtil.getInstance()
			const PhoneNumberFormat = libphonenumber.PhoneNumberFormat

			const exampleNumber = phoneUtil.getExampleNumberForType(
				selectedCountry as CountryCode,
				libphonenumber.PhoneNumberType.MOBILE
			)

			const formattedNumber = phoneUtil.format(exampleNumber, PhoneNumberFormat.INTERNATIONAL);

			// Convert the formatted number into a regex pattern
			const regexPattern = formattedNumber
					.replace(`+${callingCode}`, '')
					.replace(/\d/g, '0') // Replace all digits with \d

			this.countryPattern.set(regexPattern)
			addressCtrl.controls.phone_number.setValidators(Validators.required)
		} else {
			addressCtrl.controls.phone_number.setValidators([])
		}
	}

	private setStaticValidators() {
		const addressCtrl = this.shipping.controls.address
		const addressPattern = /^[A-Za-z0-9\s\-\.,/#]*$/
		const namePattern = /^[A-Za-z\s-]*$/
		const addressValidatorsWithRequired = [Validators.required, Validators.pattern(addressPattern)]
		const addressValidatorsWithoutRequired = [Validators.pattern(addressPattern)]
		const nameValidators = [Validators.required, Validators.pattern(namePattern)]

		addressCtrl.controls.address_1.setValidators(addressValidatorsWithRequired)
		addressCtrl.controls.address_2.setValidators(addressValidatorsWithoutRequired)
		addressCtrl.controls.city.setValidators(nameValidators)
		addressCtrl.controls.state.setValidators(nameValidators)
		addressCtrl.controls.country.setValidators([Validators.required])

		addressCtrl.controls.address_1.updateValueAndValidity()
		addressCtrl.controls.address_2.updateValueAndValidity()
		addressCtrl.controls.city.updateValueAndValidity()
		addressCtrl.controls.state.updateValueAndValidity()
		addressCtrl.controls.country.updateValueAndValidity()
	}

	public onCountryChange(event: MatAutocompleteSelectedEvent) {
		this.resetMap()
		this.shipping.controls.address.controls.phone_number.reset()
		this.shipping.controls.address.controls.state.reset()
		this.setCountryValidators(event.option.value)
		this.listenToIdpChanges()
	}

	private resetMap() {
		this.mapURL = null
		this.recalledAddress = null
	}
}
